import {
  Dispatch,
  forwardRef,
  HTMLAttributes,
  MouseEvent,
  MouseEventHandler,
  Ref,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import Lock from "@mui/icons-material/Lock";
import { alpha, Box, CircularProgress, Tooltip } from "@mui/material";
import {
  RichTreeView,
  TreeItem2Content,
  TreeItem2GroupTransition,
  TreeItem2Icon,
  TreeItem2IconContainer,
  TreeItem2Provider,
  TreeItem2Root,
  TreeViewBaseItem,
  UseTreeItem2Parameters,
  useTreeViewApiRef,
} from "@mui/x-tree-view";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2/useTreeItem2";
import FileTitle from "files/components/FileTitle";
import useExternalBuckets from "files/hooks/useExternalBuckets";
import useFileAccess from "files/hooks/useFileAccess";
import useFiles, {
  encodeObjectKey,
  resolveObjectKey,
  resolveRouterPath,
} from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { useSWRConfig } from "swr";
import Loading from "~/components/Loading";
import TextOverflow from "~/components/TextOverflow";
import useUser from "~/contexts/user";
import useFilePermissions from "~/hooks/useFilePermissions";
import { FILE_BROWSER_API } from "~/lib/fileBrowserApi";
import theme from "~/theme";

type ItemProps = TreeViewBaseItem<FileInfo>;

interface CustomTreeItemProps
  extends Omit<UseTreeItem2Parameters, "rootRef">, Omit<HTMLAttributes<HTMLLIElement>, "onFocus"> {
  slotProps?: any;
  checkWriteAccess?: boolean;
}

const Item = forwardRef(function Item(props: CustomTreeItemProps, ref: Ref<HTMLLIElement>) {
  const user = useUser();
  const { cache } = useSWRConfig();
  const { id, itemId, label, children, slotProps, checkWriteAccess, ...other } = props;
  const objectKey = resolveObjectKey(resolveRouterPath(itemId), user?.email);
  const fileFetchUrl = `${FILE_BROWSER_API}/files/${encodeObjectKey(objectKey)}`;
  const loading = cache.get(fileFetchUrl)?.isLoading;
  const canWrite = useFileAccess(objectKey, "write");
  const disabled = itemId !== BaseDirectory.ORG && !canWrite;
  const {
    publicAPI,
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  const fileInfo = (publicAPI as any).getItem(itemId);
  const isFile = fileInfo?.type === FileType.FILE;
  const contentProps = getContentProps();

  // Check if this item has loaded children in the data structure
  const dataHasChildren =
    fileInfo?.children && Array.isArray(fileInfo.children) && fileInfo.children.length > 0;
  const hasChildren = Array.isArray(children) && children.length > 0;

  // Show expansion when either React children exist OR data children exist
  const expanded = status.expanded && (hasChildren || dataHasChildren);

  return (
    <TreeItem2Provider itemId={itemId}>
      <TreeItem2Root {...getRootProps(other)}>
        <TreeItem2Content
          {...contentProps}
          sx={{
            fontSize: 14,
            minWidth: 100,
            userSelect: "none",
            bgcolor: status.selected
              ? `${alpha(theme.palette.primary.main, 0.1)} !important`
              : "initial",
          }}
        >
          {isFile ? (
            <InsertDriveFileOutlined sx={{ color: "primary.dark", opacity: 0.6, fontSize: 14 }} />
          ) : (
            <TreeItem2IconContainer
              {...getIconContainerProps()}
              onClick={(e) => slotProps?.iconContainer?.onClick(e, itemId)}
              sx={{
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.07)",
                  borderRadius: 1,
                },
              }}
            >
              <TreeItem2Icon
                status={{
                  ...status,
                  // Fix: Show expandable icon for all directories, not just those with loaded children
                  expandable: !isFile,
                }}
              />
            </TreeItem2IconContainer>
          )}
          <TextOverflow autoTooltip>
            <FileTitle fileKey={itemId} />
          </TextOverflow>
          {checkWriteAccess && disabled && (
            <Tooltip title="You do not have permission to write to this folder">
              <Lock sx={{ fontSize: 12, opacity: 0.3 }} />
            </Tooltip>
          )}
          {loading && <CircularProgress size={10} color="inherit" />}
        </TreeItem2Content>
        <TreeItem2GroupTransition {...getGroupTransitionProps()} in={expanded} />
      </TreeItem2Root>
    </TreeItem2Provider>
  );
});

const updateTreeData = (
  root: ItemProps[],
  path: string,
  files: FileInfo[],
  showFiles?: boolean
): ItemProps[] => {
  if (!path) {
    // Start with root level virtual directories
    return files;
  }

  return root.map((item) => {
    // If showFiles is enabled, include all items; otherwise, only directories.
    const items = showFiles ? files : files.filter(({ type }) => type === FileType.DIRECTORY);

    if (item.key === path && !item.children) {
      return {
        ...item,
        children: items,
      };
    }
    if (item.children) {
      return {
        ...item,
        children: updateTreeData(item.children, path, files, showFiles),
      };
    }
    return item;
  });
};

interface FileTreeProps {
  selectedFile: FileInfo | null;
  setSelectedFile: Dispatch<SetStateAction<FileInfo | null>>;
  showFiles?: boolean;
  orgFilesOnly?: boolean;
  checkWriteAccess?: boolean;
  maxHeight?: number;
}

const FileTree = ({
  selectedFile,
  setSelectedFile,
  showFiles,
  orgFilesOnly,
  checkWriteAccess,
  maxHeight,
}: FileTreeProps) => {
  const user = useUser();
  const apiRef = useTreeViewApiRef();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [treeItems, setTreeItems] = useState<ItemProps[]>([]);
  const lastExpandedPath = expanded.at(-1) ?? "";
  const fetchKey = resolveObjectKey(resolveRouterPath(lastExpandedPath), user?.email);
  const { isLoading: loadingPermissions } = useFilePermissions();
  const { data: directoryData, isLoading: loadingFiles } = useFiles(fetchKey, true);
  const { data: externalBuckets, isLoading: loadingBuckets } = useExternalBuckets();

  const showLoading = !fetchKey && (loadingPermissions || loadingFiles || loadingBuckets);

  useEffect(() => {
    if (!fetchKey || `${fetchKey}/` === directoryData?.current_file?.key) {
      // Add new entries to tree as directories are expanded and new file list response data cones back
      setTreeItems((prev) => {
        if (directoryData?.current_file && directoryData?.children) {
          let directories = [...directoryData.children];

          // If at root, add external buckets
          if (fetchKey === "") {
            if (orgFilesOnly) {
              directories = directories.filter(({ key }) => key === `${BaseDirectory.ORG}/`);
            }

            if (externalBuckets) {
              directories.push(
                ...externalBuckets.map(({ bucket_name }) => ({
                  key: `${bucket_name}/`,
                  type: FileType.DIRECTORY,
                }))
              );
            }
          }

          return updateTreeData(prev, lastExpandedPath, directories, showFiles);
        }
        return prev;
      });
    }
  }, [directoryData, fetchKey, externalBuckets, orgFilesOnly]);

  const handleExpansionIconClick = (_: MouseEvent, selectedId: string) => {
    if (expanded.includes(selectedId)) {
      setExpanded((prev) => prev.filter((itemId) => itemId !== selectedId));
    } else {
      setExpanded((prev) => [...prev, selectedId]);
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        height: maxHeight ?? 400,
        overflowY: "auto",
        borderRadius: 1.5,
        border: ({ palette }) => `1px solid ${palette.grey[300]}`,
      }}
    >
      {showLoading ? (
        <Loading />
      ) : (
        <RichTreeView
          apiRef={apiRef}
          items={treeItems}
          itemChildrenIndentation={20}
          expandedItems={expanded}
          selectedItems={selectedFile?.key ?? ""}
          onItemSelectionToggle={(_, itemId) => {
            if (itemId) {
              const fileInfo = (apiRef.current as any)?.getItem(itemId);
              if (showFiles) {
                // Only allow files to be selected, not directories
                if (fileInfo?.type === FileType.FILE) {
                  setSelectedFile(fileInfo);
                }
              } else {
                setSelectedFile(fileInfo);
              }
            }
          }}
          getItemId={(file) => file.key}
          getItemLabel={(file) => file.key}
          slots={{ item: Item }}
          slotProps={{
            item: {
              checkWriteAccess,
              slotProps: {
                iconContainer: {
                  onClick: handleExpansionIconClick as MouseEventHandler,
                },
              },
            } as any, // Currently MUI treeview struggles with custom slots + slotProps
            // see more here: https://github.com/mui/mui-x/issues/13351#issuecomment-2144933097
          }}
        />
      )}
    </Box>
  );
};

export default FileTree;
